import React, { useState, useEffect, useRef } from 'react';
import { TextInput, Button, EmptyState, MessageTextSquare01, Ticket01, Gift01, Clock } from '@opendate-app/setlist';

const menuStyle = {
  marginTop: 4,
  minWidth: 140,
  border: '1px solid var(--sl-border-secondary)',
  borderRadius: 8,
  boxShadow: '0 12px 16px -4px rgba(10,13,18,0.08), 0 4px 6px -2px rgba(10,13,18,0.03), 0 2px 2px -1px rgba(10,13,18,0.04)',
  padding: '4px 0',
  backgroundColor: 'var(--sl-bg-primary)',
  position: 'absolute',
  right: 0,
  top: '100%',
  zIndex: 9999,
};

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'notes', label: 'Notes' },
  { key: 'tickets', label: 'Tickets' },
  { key: 'giving', label: 'Giving' },
];

const TYPE_BY_FILTER = {
  notes: 'note',
  tickets: 'order',
  giving: 'giving',
};

const ICON_BY_TYPE = {
  note: MessageTextSquare01,
  order: Ticket01,
  giving: Gift01,
};

function ActivityFeed({ feed = [], counts = {}, loading = false, onCreateNote, onEditNote, onDeleteNote, onUploadAttachment, showTickets = true }) {
  const [filter, setFilter] = useState('all');
  const filters = FILTERS.filter((f) => showTickets || f.key !== 'tickets');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [composerError, setComposerError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!openMenuId) return undefined;
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) setOpenMenuId(null);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  const visibleFeed = filter === 'all'
    ? feed
    : feed.filter((row) => row.type === TYPE_BY_FILTER[filter]);

  const handleAdd = async () => {
    const value = body.trim();
    if (!value || !onCreateNote || submitting) return;
    setSubmitting(true);
    setComposerError('');
    try {
      await onCreateNote(value);
      setBody('');
    } catch (err) {
      setComposerError('Failed to add note');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file || !onUploadAttachment || uploading) return;
    setUploading(true);
    setComposerError('');
    try {
      await onUploadAttachment(file);
    } catch (err) {
      setComposerError('Failed to upload attachment');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="cdp-card">
      <div className="cdp-card-header-row">
        <h2 className="cdp-card-title">Activity</h2>
        <div className="cdp-pill-row">
          {filters.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`cdp-pill cdp-pill-filter${filter === f.key ? ' cdp-pill-filter--active' : ''}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label} ({counts[f.key] || 0})
            </button>
          ))}
        </div>
      </div>

      {onCreateNote && (
        <div className="cdp-activity-composer">
          <TextInput
            multiline
            rows={2}
            placeholder="Add a note…"
            value={body}
            disabled={submitting}
            onChange={(e) => { setBody(e.target.value); setComposerError(''); }}
          />
          {composerError && <div className="cdp-activity-composer-error">{composerError}</div>}
          <div className="cdp-activity-composer-actions">
            <div>
              {onUploadAttachment && (
                <>
                  <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileChange} />
                  <button
                    type="button"
                    className="cdp-activity-attach-btn"
                    aria-label="Add attachment"
                    disabled={uploading}
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  >
                    <i className="fal fa-paperclip" aria-hidden="true" />
                    <span>{uploading ? 'Uploading…' : 'Attach file'}</span>
                  </button>
                </>
              )}
            </div>
            <Button variant="primary" onClick={handleAdd} disabled={!body.trim() || submitting}>
              {submitting ? 'Adding…' : 'Add note'}
            </Button>
          </div>
        </div>
      )}

      {!loading && visibleFeed.length === 0 && (
        <EmptyState size="sm" icon={Clock} title="No activity yet." />
      )}

      {visibleFeed.length > 0 && (
        <ol className="cdp-activity-list">
          {visibleFeed.map((row) => {
            const Icon = ICON_BY_TYPE[row.type] || Clock;
            const noteActions = row.type === 'note' && row.note && (onEditNote || onDeleteNote);
            return (
              <li key={row.id} className="cdp-activity-row">
                <span className="cdp-activity-icon">
                  {row.type === 'files'
                    ? <i className="fal fa-paperclip" aria-hidden="true" />
                    : <Icon width={16} height={16} aria-hidden="true" focusable="false" />}
                </span>
                <div className="cdp-activity-row-body">
                  <div className="cdp-activity-row-header">
                    {row.href ? (
                      <a href={row.href} className="cdp-activity-title cdp-activity-title-link">{row.title}</a>
                    ) : (
                      <span className="cdp-activity-title">{row.title}</span>
                    )}
                    <span className="cdp-activity-meta">{row.meta}</span>
                    {noteActions && (
                      <div
                        ref={openMenuId === row.id ? menuRef : null}
                        className="position-relative"
                        style={{ marginLeft: 'auto' }}
                      >
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 text-muted"
                          aria-label="More note actions"
                          onClick={() => setOpenMenuId((current) => (current === row.id ? null : row.id))}
                        >
                          <i className="fal fa-ellipsis-v" aria-hidden="true" />
                        </button>
                        {openMenuId === row.id && (
                          <div className="dropdown-menu show" style={menuStyle}>
                            {onEditNote && (
                              <button
                                type="button"
                                className="cdp-activity-menu-item"
                                onClick={() => { setOpenMenuId(null); onEditNote(row.note); }}
                              >
                                <i className="fal fa-pencil" aria-hidden="true" />
                                <span>Edit note</span>
                              </button>
                            )}
                            {onDeleteNote && (
                              <button
                                type="button"
                                className="cdp-activity-menu-item cdp-activity-menu-item--danger"
                                onClick={() => { setOpenMenuId(null); onDeleteNote(row.note); }}
                              >
                                <i className="fal fa-trash-alt" aria-hidden="true" />
                                <span>Delete note</span>
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="cdp-activity-text">{row.body}</div>
                  {row.files && row.files.length > 0 && (
                    <div className="cdp-activity-files">
                      {row.files.map((f) => (
                        <a
                          key={f.id}
                          href={f.download_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="cdp-activity-file-chip"
                        >
                          <span className="cdp-activity-file-chip-icon"><i className="fal fa-file" aria-hidden="true" /></span>
                          <span className="cdp-activity-file-chip-body">
                            <span className="cdp-activity-file-chip-name">{f.file_name}</span>
                            <span className="cdp-activity-file-chip-size">{f.human_size}</span>
                          </span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

export default ActivityFeed;
